import { useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import {
  Archive,
  BookOpen,
  Copy,
  History,
  Lock,
  Info,
  MoreHorizontal,
  Trash,
  Trash2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { Application } from '@/api/types'
import { ApplicationAccessDialog } from '@/features/application/access/access-dialog'
import {
  useDeleteApplication,
  useUpdateApplication,
} from '@/features/application/queries'
import { useDuplicateApplication } from '@/features/job/queries'
import { ActivityDialog } from '@/features/database/history/components/activity-dialog'
import { SnapshotsDialog } from '@/features/snapshot/components/snapshots-dialog'
import { TrashDialog } from '@/features/trash/components/trash-dialog'
import { useWorkspace } from '@/features/workspace/queries'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'
import { DetailsDialog } from '@/shared/components/details-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu'
import { SidebarMenuAction } from '@/shared/ui/sidebar'

/**
 * What can be done to one application, from the sidebar row itself.
 *
 * Deleting is confirmed but not warned about at length: it trashes rather than
 * destroys, and the trash below it can put it back for as long as
 * `HOURS_UNTIL_TRASH_PERMANENTLY_DELETED` allows -- which is also why the trash
 * is offered here and not only on the workspace. What somebody wants back is
 * almost always something they deleted from the thing they are looking at.
 */
export function ApplicationActions({
  workspaceId,
  application,
}: {
  workspaceId: number
  application: Application
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const [dialog, setDialog] = useState<
    'details' | 'delete' | 'trash' | 'activity' | 'snapshots' | 'access' | null
  >(null)

  // Narrowed once rather than at each of the three places that need it: only a
  // database has tables, and everything below that reads one is about tables.
  const database = application.type === 'database' ? application : null

  const { data: workspace } = useWorkspace(workspaceId)
  const update = useUpdateApplication()
  const remove = useDeleteApplication()
  const duplicate = useDuplicateApplication()

  /**
   * Whether the route being viewed lives inside the application being deleted.
   *
   * Checked before the delete rather than after: the optimistic removal takes the
   * application out of the cache immediately, and by the time the request settles
   * there is nothing left to compare the route against.
   */
  const viewing =
    params.databaseId === application.id || params.dashboardId === application.id

  function confirmDelete() {
    // The promise rather than a `mutate` callback: the optimistic removal
    // unmounts this row, and `mutate` drops its callbacks once that happens.
    // A failure is reported by the global mutation error handler.
    remove.mutateAsync({ workspaceId, applicationId: application.id }).then(
      () => {
        toast.success(t('application.deleted', { name: application.name }))
        if (viewing) void navigate({ to: '/dashboard' })
      },
      () => {},
    )
    setDialog(null)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            showOnHover
            aria-label={t('application.actions', { name: application.name })}
          >
            <MoreHorizontal />
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right">
          {/*
            A database's endpoints are documented from the reader's own tables,
            so the reference is per database rather than one page for the
            instance -- which is why the link belongs on the application and not
            in the sidebar's own navigation.
          */}
          {database !== null && (
            <DropdownMenuItem asChild>
              <Link
                to="/api-docs/database/$databaseId"
                params={{ databaseId: database.id }}
              >
                <BookOpen />
                {t('apiDocs.title')}
              </Link>
            </DropdownMenuItem>
          )}
          {/*
            Only a database has one. A dashboard holds no rows of its own -- it
            reads them from a database, whose activity history already records
            every change it could show.
          */}
          {database !== null && (
            <DropdownMenuItem onSelect={() => setDialog('activity')}>
              <History />
              {t('activity.title')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog('details')}>
            <Info />
            {t('action.details')}
          </DropdownMenuItem>
          {/*
            Not confirmed, and nothing optimistic. The server answers with a job
            rather than an application, and what it will eventually be called is
            its decision -- a placeholder row would be guessing at a name.
          */}
          <DropdownMenuItem
            disabled={duplicate.isPending}
            onSelect={() => duplicate.mutate(application.id)}
          >
            <Copy />
            {t('action.duplicate')}
          </DropdownMenuItem>
          {/*
            Offered for a dashboard as well as a database: a snapshot copies
            whatever application it is pointed at, and a dashboard's widgets and
            data sources are as much worth keeping as a table's rows.
          */}
          <DropdownMenuItem onSelect={() => setDialog('snapshots')}>
            <Archive />
            {t('snapshot.title')}
          </DropdownMenuItem>
          {/*
            Offered to the people the server would actually let through -- the
            application's creator and the workspace admins -- rather than to
            everyone and refused on submit. The answer comes down on the
            application itself, so no extra request is needed to ask it.
          */}
          {application.user_can_configure_access && (
            <DropdownMenuItem onSelect={() => setDialog('access')}>
              <Lock />
              {t('applicationAccess.menu')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => setDialog('trash')}>
            <Trash />
            {t('trash.open')}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={() => setDialog('delete')}>
            <Trash2 />
            {t('action.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'details' && (
        <DetailsDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t(`application.details.${application.type}`)}
          nameLabel={t('application.name')}
          descriptionLabel={t('application.description')}
          descriptionHint={t('application.descriptionHint')}
          current={{ name: application.name, description: application.description ?? '' }}
          pending={update.isPending}
          onSubmit={(details) => {
            update.mutate({ workspaceId, applicationId: application.id, ...details })
            // Closed without waiting: the cache already has the new details, so
            // holding the dialog open would only show a spinner over a sidebar
            // that has finished changing.
            setDialog(null)
          }}
        />
      )}

      {/*
        The workspace is looked up rather than passed in, and it is always
        there: `ApplicationList` is only rendered once the selected workspace
        has resolved out of the same cached list, so a row cannot exist before
        its workspace does. The check is what says so to the type checker --
        guarding the menu item with it as well would be the same condition
        written twice, and no test could tell the two apart.
      */}
      {dialog === 'trash' && workspace !== undefined && (
        <TrashDialog
          workspace={workspace}
          initialApplicationId={application.id}
          open
          onOpenChange={() => setDialog(null)}
        />
      )}

      {dialog === 'activity' && database !== null && (
        <ActivityDialog
          database={database}
          workspaceId={workspaceId}
          open
          onOpenChange={() => setDialog(null)}
        />
      )}

      {dialog === 'access' && (
        <ApplicationAccessDialog
          workspaceId={workspaceId}
          applicationId={application.id}
          applicationName={application.name}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog === 'snapshots' && (
        <SnapshotsDialog
          applicationId={application.id}
          applicationName={application.name}
          open
          onOpenChange={() => setDialog(null)}
        />
      )}

      {dialog === 'delete' && (
        <ConfirmDialog
          open
          onOpenChange={() => setDialog(null)}
          title={t('application.deleteTitle', { name: application.name })}
          description={t('application.deleteDescription')}
          confirmLabel={t('action.delete')}
          destructive
          onConfirm={confirmDelete}
        />
      )}
    </>
  )
}
